import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  AssignmentInd as AssignmentIndIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  SupervisorAccount as SupervisorIcon,
  Inventory2 as BatchIcon,
  PersonOff as UnassignedIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import {
  useBirdProcurementAssignees,
  useCreateBirdProcurementAssignee,
  useDeleteBirdProcurementAssignee,
  useSupervisors,
} from '../hooks/useBirdProcurementAssignees';
import { useBirdProcurements } from '../hooks/useBirds';
import { BirdProcurementAssignee } from '../types';
import { format } from 'date-fns';

// ─── Design tokens ───────────────────────────────────────────────────────────

const dataGridSx = {
  border: 'none',
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.06)',
    borderBottom: '1px solid rgba(255,152,0,0.15)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.65)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row': {
    minHeight: '72px !important',
    '&:hover': { background: 'rgba(255,152,0,0.04)' },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.35)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.12)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 160 },
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: string;
  Icon: React.ElementType;
}> = ({ label, value, sub, accent, Icon }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      background: '#111318',
      border: '1px solid rgba(255,255,255,0.05)',
      borderLeft: `3px solid ${accent}`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Icon
      sx={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 52,
        color: `${accent}0D`,
      }}
    />
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.7px',
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        display: 'block',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Typography
      fontWeight={800}
      sx={{ color: accent, lineHeight: 1, fontSize: '2rem', mb: sub ? 0.5 : 0 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.65rem' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ─── Main ─────────────────────────────────────────────────────────────────────

const BirdProcurementAssigneesPage: React.FC = () => {
  const [addDialog, setAddDialog] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<BirdProcurementAssignee | null>(null);
  const [formData, setFormData] = useState<{
    procurement_ids: number[];
    assignee_id: number | null;
  }>({
    procurement_ids: [],
    assignee_id: null,
  });

  const { data: assignments = [], isLoading, isFetching, refetch } = useBirdProcurementAssignees();
  const { data: procurementsPage, isFetching: procFetching } = useBirdProcurements({
    per_page: 100,
  });
  const { data: supervisors = [], isFetching: supFetching } = useSupervisors();
  const createMutation = useCreateBirdProcurementAssignee();
  const deleteMutation = useDeleteBirdProcurementAssignee();

  const procurements = (procurementsPage as any)?.data ?? [];

  const stats = useMemo(() => {
    const assignedProcIds = new Set(
      (assignments as BirdProcurementAssignee[]).map(
        (a) => a.bird_procurement_id ?? (a as any).procurement?.id
      )
    );
    return {
      total: assignments.length,
      supervisors: new Set((assignments as any[]).map((a) => a.assignee?.id)).size,
      assigned: assignedProcIds.size,
      unassigned: procurements.filter((p: any) => !assignedProcIds.has(p.id)).length,
    };
  }, [assignments, procurements]);

  const handleAdd = async () => {
    if (!formData.assignee_id || !formData.procurement_ids.length) return;
    try {
      await createMutation.mutateAsync({
        procurement_ids: formData.procurement_ids,
        assignee_id: formData.assignee_id,
      });
      setAddDialog(false);
      setFormData({ procurement_ids: [], assignee_id: null });
    } catch (e) {
      console.error(e);
    }
  };

  const handleRelease = async () => {
    if (!releaseTarget) return;
    try {
      await deleteMutation.mutateAsync(releaseTarget.id);
      setReleaseTarget(null);
    } catch (e) {
      console.error(e);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'procurement',
      headerName: 'Procurement Batch',
      flex: 2,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<BirdProcurementAssignee>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              flexShrink: 0,
              background: 'rgba(255,152,0,0.1)',
              border: '1px solid rgba(255,152,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BatchIcon sx={{ color: '#ff9800', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.3 }}
            >
              {params.row.procurement?.identifier ?? 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
              {params.row.procurement != null && (
                <Chip
                  label={`${Number(
                    (params.row.procurement.birds as any[] | undefined)?.reduce(
                      (s: number, b: any) => s + (b.current_quantity ?? 0),
                      0
                    ) ??
                      params.row.procurement.total_quantity ??
                      0
                  ).toLocaleString()} birds`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,152,0,0.1)',
                    color: '#ffa726',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    height: 18,
                  }}
                />
              )}
              {params.row.procurement?.date_procured && (
                <Chip
                  label={params.row.procurement.date_procured}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(33,150,243,0.08)',
                    color: '#64b5f6',
                    fontSize: '0.68rem',
                    height: 18,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'assignee',
      headerName: 'Supervisor',
      flex: 1.8,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<BirdProcurementAssignee>) => {
        const name: string = (params.row as any).assignee?.name ?? 'N/A';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(76,175,80,0.12)',
                color: '#66bb6a',
                width: 36,
                height: 36,
                fontSize: '0.85rem',
                fontWeight: 800,
                border: '1.5px solid rgba(76,175,80,0.2)',
                flexShrink: 0,
              }}
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography
                sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.3 }}
              >
                {name}
              </Typography>
              {(params.row as any).assignee?.role?.role_code && (
                <Chip
                  label={(params.row as any).assignee.role.role_code}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(76,175,80,0.12)',
                    color: '#81c784',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    height: 18,
                    mt: 0.4,
                    textTransform: 'capitalize',
                  }}
                />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Assigned',
      flex: 1,
      minWidth: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<BirdProcurementAssignee>) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
          {params.row.created_at ? format(new Date(params.row.created_at), 'MMM dd, yyyy') : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.5,
      minWidth: 70,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<BirdProcurementAssignee>) => (
        <Tooltip title="Remove assignment" arrow>
          <IconButton
            size="small"
            onClick={() => setReleaseTarget(params.row)}
            sx={{
              color: '#f44336',
              bgcolor: 'rgba(244,67,54,0.08)',
              border: '1px solid rgba(244,67,54,0.18)',
              width: 32,
              height: 32,
              '&:hover': { bgcolor: 'rgba(244,67,54,0.18)', borderColor: 'rgba(244,67,54,0.35)' },
            }}
          >
            <PersonRemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || procFetching || supFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            bgcolor: 'rgba(255,152,0,0.1)',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffa726)' },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #131316 0%, #1c1c22 100%)',
          border: '1px solid rgba(255,152,0,0.15)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 2,
            flexShrink: 0,
            background:
              'linear-gradient(135deg, rgba(255,152,0,0.2) 0%, rgba(255,152,0,0.08) 100%)',
            border: '1px solid rgba(255,152,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AssignmentIndIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.2 }}
          >
            Procurement Assignments
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.3 }}>
            Assign supervisors to bird procurement batches and sync batch ownership
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            color: '#000',
            fontWeight: 700,
            borderRadius: 2,
            px: 2.5,
            boxShadow: '0 4px 18px rgba(255,152,0,0.35)',
            flexShrink: 0,
            '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
          }}
        >
          Assign Supervisor
        </Button>
      </Box>

      {/* ── Stats ── */}
      <Box display="flex" gap={2.5} mb={3} flexWrap="wrap">
        <StatCard
          label="Total Assignments"
          value={stats.total}
          sub="active records"
          accent="#ff9800"
          Icon={AssignmentIndIcon}
        />
        <StatCard
          label="Supervisors Assigned"
          value={stats.supervisors}
          sub="unique supervisors"
          accent="#66bb6a"
          Icon={SupervisorIcon}
        />
        <StatCard
          label="Batches Covered"
          value={stats.assigned}
          sub="procurements with supervisor"
          accent="#42a5f5"
          Icon={GroupsIcon}
        />
        <StatCard
          label="Unassigned Batches"
          value={stats.unassigned}
          sub="need a supervisor"
          accent={stats.unassigned > 0 ? '#f44336' : '#66bb6a'}
          Icon={UnassignedIcon}
        />
      </Box>

      {/* ── Table ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: '1px solid rgba(255,152,0,0.12)',
          background: '#0e0e10',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'rgba(255,152,0,0.04)',
          }}
        >
          <AssignmentIndIcon sx={{ color: '#ff9800', fontSize: 18 }} />
          <Typography fontWeight={700} sx={{ color: '#fff', fontSize: '0.9rem' }}>
            All Assignments
          </Typography>
          <Chip
            label={assignments.length}
            size="small"
            sx={{
              bgcolor: 'rgba(255,152,0,0.12)',
              color: '#ff9800',
              fontWeight: 700,
              height: 20,
              fontSize: '0.72rem',
            }}
          />
          <Box flex={1} />
          <Button
            size="small"
            onClick={() => refetch()}
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '0.72rem',
              textTransform: 'none',
              minWidth: 0,
            }}
          >
            Refresh
          </Button>
        </Box>
        <Box sx={{ height: assignments.length ? 'calc(100vh - 460px)' : 200, minHeight: 200 }}>
          <DataGrid
            rows={assignments as BirdProcurementAssignee[]}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            pageSizeOptions={[25, 50]}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={dataGridSx}
          />
        </Box>
      </Paper>

      {/* ══ Assign dialog ══ */}
      <Dialog
        open={addDialog}
        onClose={() => {
          setAddDialog(false);
          setFormData({ procurement_ids: [], assignee_id: null });
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(255,152,0,0.2)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
            Assign Supervisor
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
            The batch&apos;s supervisor_id will be updated automatically
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            multiple
            options={procurements}
            getOptionLabel={(o: any) =>
              `${o.identifier}${(() => {
                const cur =
                  (o.birds as any[] | undefined)?.reduce(
                    (s: number, b: any) => s + (b.current_quantity ?? 0),
                    0
                  ) ?? o.total_quantity;
                return cur ? ` · ${Number(cur).toLocaleString()} birds` : '';
              })()} `
            }
            value={procurements.filter((p: any) => formData.procurement_ids.includes(p.id))}
            onChange={(_, val: any[]) =>
              setFormData((f) => ({ ...f, procurement_ids: val.map((p) => p.id) }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Procurement Batches"
                placeholder="Select one or more batches"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
                }}
              />
            )}
            slotProps={{
              chip: {
                size: 'small',
                sx: {
                  bgcolor: 'rgba(255,152,0,0.15)',
                  color: '#ffa726',
                  fontWeight: 600,
                  border: '1px solid rgba(255,152,0,0.25)',
                },
              },
            }}
          />
          <Autocomplete
            options={supervisors}
            getOptionLabel={(s: any) => s.name ?? `User #${s.id}`}
            value={supervisors.find((s: any) => s.id === formData.assignee_id) ?? null}
            onChange={(_, val: any) => setFormData((f) => ({ ...f, assignee_id: val?.id ?? null }))}
            renderOption={(props, option: any) => (
              <Box
                component="li"
                {...props}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '10px !important' }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'rgba(76,175,80,0.15)',
                    color: '#66bb6a',
                    width: 32,
                    height: 32,
                    fontSize: '0.8rem',
                    fontWeight: 800,
                  }}
                >
                  {(option.name ?? '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff' }}>
                    {option.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                    {option.role?.role_code ?? 'supervisor'}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supervisor"
                placeholder="Select a supervisor"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions
          sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Button
            onClick={() => {
              setAddDialog(false);
              setFormData({ procurement_ids: [], assignee_id: null });
            }}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={
              !formData.procurement_ids.length || !formData.assignee_id || createMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: '#000',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
              '&:disabled': { background: 'rgba(255,152,0,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {createMutation.isPending
              ? 'Assigning…'
              : `Assign${formData.procurement_ids.length > 1 ? ` (${formData.procurement_ids.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Release dialog ══ */}
      <Dialog
        open={!!releaseTarget}
        onClose={() => setReleaseTarget(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(244,67,54,0.25)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(244,67,54,0.12)',
            background: 'linear-gradient(135deg, rgba(244,67,54,0.07) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#f44336' }}>
            Remove Assignment
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
            The batch&apos;s supervisor will be updated to the next assigned supervisor, or cleared
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          {releaseTarget && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'rgba(244,67,54,0.06)',
                borderRadius: 2,
                border: '1px solid rgba(244,67,54,0.18)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(244,67,54,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.65rem',
                }}
              >
                Batch
              </Typography>
              <Typography fontWeight={700} sx={{ color: '#fff', mb: 1.5 }}>
                {(releaseTarget as any).procurement?.identifier ?? '—'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(244,67,54,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.65rem',
                }}
              >
                Supervisor
              </Typography>
              <Typography fontWeight={700} sx={{ color: '#fff' }}>
                {(releaseTarget as any).assignee?.name ?? '—'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(244,67,54,0.1)' }}>
          <Button
            onClick={() => setReleaseTarget(null)}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRelease}
            disabled={deleteMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #ef5350 0%, #f44336 100%)' },
            }}
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BirdProcurementAssigneesPage;
